import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Plane, FileText, CreditCard, Building2, HardHat, TrendingUp } from "lucide-react";

const Business = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) element.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[60vh] bg-black flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://static.wixstatic.com/media/e92216_73f3f16545264ed99b9c8d0bfe188264~mv2.jpg/v1/fill/w_1904,h_470,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/e92216_73f3f16545264ed99b9c8d0bfe188264~mv2.jpg')] bg-cover bg-center opacity-60" />
        <div className="relative container mx-auto px-4 text-center z-10">
          <h2 className="text-[#D97706] font-heading font-bold tracking-widest uppercase mb-4 text-xl">ᓲᓂᔮᐤ SŌNIYĀW</h2>
          <h1 className="text-5xl md:text-7xl font-heading font-bold text-white uppercase mb-6">Frog Lake Business</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            From enticing tax incentives to streamlined application processes, our dedicated team is here to help investors succeed.
          </p>
        </div>
      </div>

      {/* Investing Section */}
      <section id="investing" className="py-20 bg-zinc-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-heading font-bold uppercase mb-4">Investing in Frog Lake</h2>
            <div className="h-1 w-20 bg-[#D97706] mx-auto" />
            <p className="mt-6 text-gray-600 max-w-3xl mx-auto">
              Choose to invest with us and take advantage of streamlined processes and great benefits to get your business running quickly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              "Small, motivated council ready to help",
              "Same-day permit approvals",
              "Construction start within a week",
              "Dedicated economic development committee",
              "Lowest corporate tax rate (10%)",
              "No provincial sales tax",
              "High GDP growth",
              "High labour productivity",
              "Investment per capita 2x Canadian average"
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded border border-zinc-200 flex items-start gap-4">
                <div className="h-8 w-8 bg-[#D97706]/10 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-[#D97706] font-bold">{i + 1}</span>
                </div>
                <p className="font-medium text-zinc-800">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-gray-700 mb-6">
              Discuss investing: Call <strong>780-943-3737</strong> or email <a href="mailto:williamquinney@froglake.ca" className="text-[#D97706] hover:underline">williamquinney@froglake.ca</a>
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section id="directory" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-heading font-bold uppercase mb-12 text-center">Home to Great Industries</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "Industrial Hemp", icon: HardHat },
              { name: "Oil & Gas", icon: TrendingUp },
              { name: "Agriculture", icon: CheckCircle },
              { name: "Tourism", icon: Plane },
              { name: "Financial Services", icon: CreditCard },
              { name: "Agri-Foods", icon: CheckCircle },
            ].map((ind, i) => (
              <div key={i} className="bg-zinc-50 p-6 flex flex-col items-center text-center hover:bg-zinc-100 transition-colors cursor-pointer group">
                <ind.icon className="h-8 w-8 text-gray-400 group-hover:text-[#D97706] mb-3 transition-colors" />
                <span className="font-bold text-sm uppercase">{ind.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bids & Tenders */}
      <section id="bids" className="py-20 bg-zinc-900 text-white">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-heading font-bold uppercase mb-6">Bid & Tender Opportunities</h2>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Our Procurement Department manages acquisition of goods and services. We advertise procurement request documents including bids, quotations, and proposals.
            </p>
            <div className="bg-zinc-800 p-6 rounded border-l-4 border-[#D97706]">
              <h4 className="font-bold uppercase mb-2 text-[#D97706]">Vendor Notice</h4>
              <p className="text-sm text-gray-300">
                All vendors are encouraged to create a profile on <strong>bidsandtenders.ca</strong> to receive notices and submit bids electronically.
              </p>
            </div>
            <div className="mt-8">
              <Button asChild className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none">
                <a href="https://www.bidsandtenders.com/" target="_blank" rel="noreferrer">View Opportunities</a>
              </Button>
            </div>
          </div>
          <div className="h-80 bg-zinc-800 rounded flex items-center justify-center">
            <FileText className="h-24 w-24 text-zinc-700" />
          </div>
        </div>
      </section>

      {/* Airport */}
      <section id="airport" className="py-20 bg-white">
        <div className="container mx-auto px-4 flex flex-col md:flex-row gap-12">
          <div className="md:w-1/2">
            <img src="https://static.wixstatic.com/media/e92216_4266af8ae99c4043b1a4439ac39ed6d8~mv2.jpg/v1/crop/x_533,y_0,w_1225,h_1294/fill/w_478,h_505,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/airport.jpg" alt="Airport" className="w-full h-auto rounded shadow-lg" />
          </div>
          <div className="md:w-1/2 flex flex-col justify-center">
            <h2 className="text-3xl font-heading font-bold uppercase mb-6">Elk Point Airport</h2>
            <p className="text-gray-600 mb-4">
              Located approximately 8 kms east of Elk Point on secondary highway 646.
            </p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#D97706]"/> Asphalt runway 4492 ft x 75 ft</li>
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#D97706]"/> Tie down area</li>
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#D97706]"/> Terminal with lounge & kitchen</li>
            </ul>
            <p className="font-bold">Call Town Office: 780-724-3810</p>
          </div>
        </div>
      </section>

      {/* Payments */}
      <section id="payments" className="py-20 bg-zinc-50 border-t">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold uppercase mb-12">Payment Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="pt-6">
                <CreditCard className="h-10 w-10 mx-auto text-[#D97706] mb-4" />
                <h3 className="font-bold text-lg mb-2">Online</h3>
                <p className="text-sm text-gray-500">Pay bills via online banking. Receipts issued for online payments.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Building2 className="h-10 w-10 mx-auto text-[#D97706] mb-4" />
                <h3 className="font-bold text-lg mb-2">In Person</h3>
                <p className="text-sm text-gray-500">Visit Admin Office: Mon-Fri 8:30-4:30. Debit, Visa, MasterCard accepted.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <FileText className="h-10 w-10 mx-auto text-[#D97706] mb-4" />
                <h3 className="font-bold text-lg mb-2">By Mail</h3>
                <p className="text-sm text-gray-500">Send cheque/money order to General Delivery, Frog Lake, AB T0A 1M0.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Business;
